import { useMemo, useState } from "react";
import { ChevronDown, ExternalLink, Search } from "lucide-react";
import { eur } from "@/lib/price";
import { PraticaInfo } from "./VestaPraticaInfo";
import { CfgDelete } from "./VestaCfgDelete";

const STEPS = [
  ["proposta_generata", "Generata"], ["proposta_inviata_at", "Inviata"], ["proposta_aperta_at", "Aperta"],
  ["contratto_firmato_at", "Firmata"], ["caparra_pagata_at", "Caparra"],
];
const FILTRI = ["Tutte", "Nuova", ...STEPS.map(([, l]) => l)];
const SORTS = { data: "Data", prezzo: "Prezzo", margine: "Margine" };
const dt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const num = (n) => Number(n).toLocaleString("it-IT");
const eur0 = (n) => `${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 })} €`;

const stage = (c) => { const t = c.trattativa || {}; let s = -1; STEPS.forEach(([k], i) => { if (t[k]) s = i; }); return s; };
const isDraft = (c) => !c.commerciale && !(c.summary || {}).cliente && stage(c) < 0;
const partnerOf = (c) => c.installer_name || c.installer?.ragione_sociale || "";

function Progress({ c, testid }) {
  const t = c.trattativa || {};
  const s = stage(c);
  return (
    <div className="flex gap-1" data-testid={testid}>
      {STEPS.map(([k, l], i) => (
        <span key={k} title={`${l}${typeof t[k] === "string" ? ` · ${dt(t[k])}` : ""}`} data-testid={`${testid}-step-${k}`} data-done={t[k] ? "1" : "0"}
          className={`h-1 flex-1 rounded-full transition-colors ${t[k] ? (i === s ? "bg-[#E8B48F]" : "bg-[#C17F59]") : "bg-neutral-800"}`} />
      ))}
    </div>
  );
}

function Badge({ c, testid }) {
  const s = stage(c);
  if (s < 0) return <span className="shrink-0 rounded-full border border-neutral-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500" data-testid={testid}>Nuova</span>;
  const t = c.trattativa || {};
  const extra = STEPS[s][0] === "proposta_aperta_at" && t.proposta_aperture ? ` ×${t.proposta_aperture}` : "";
  return <span className="shrink-0 rounded-full border border-[#C17F59] bg-[#C17F59]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#E8B48F]" data-testid={testid}>{STEPS[s][1]}{extra}</span>;
}

const Chip = ({ label, value, accent, testid }) => (
  <div className="min-w-0 rounded-lg bg-neutral-900/70 px-2 py-1.5" data-testid={testid}>
    <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
    <div className={`truncate font-mono text-xs font-semibold sm:text-sm ${accent ? "text-[#E8B48F]" : "text-white"}`}>{value}</div>
  </div>
);

export function CfgCard({ c, net, focused, toolLabel, onOpen, torri, reload }) {
  const s = c.summary || {};
  const m = c.commerciale;
  const t = c.trattativa || {};
  const id = `vesta-tools-cfg-${c.id}`;
  const sub = [net && partnerOf(c), toolLabel, s.comune, s.potenza_kwp && `${s.potenza_kwp} kWp`, dt(c.updated_at || c.created_at)].filter(Boolean).join(" · ");
  const chips = m ? [
    ["Prezzo", eur0(m.prezzo_iva_incluso)],
    m.potenza_kwp != null && ["kWp", num(m.potenza_kwp)],
    !!m.accumulo_kwh && ["Accumulo", `${num(m.accumulo_kwh)} kWh`],
    m.margine_eur != null && ["Margine", eur0(m.margine_eur), true, "margine"],
  ].filter(Boolean) : [];
  return (
    <div className={`space-y-2.5 rounded-xl border bg-neutral-950 p-3 transition-colors hover:border-neutral-700 ${focused ? "border-[#C17F59] ring-2 ring-[#C17F59]" : "border-neutral-800"}`}
      data-testid={id} {...(focused ? { "data-focus": "1" } : {})}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><span className="truncate text-sm font-semibold">{s.cliente || toolLabel || "Configurazione"}</span><Badge c={c} testid={`${id}-badge`} />
            {c.nascosta_partner && <span className="shrink-0 rounded-full border border-red-900 bg-red-950/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-300" title={`Nascosta da ${c.nascosta_partner.da || "Partner"} il ${dt(c.nascosta_partner.at)}`} data-testid={`${id}-nascosta`}>Nascosta dal partner</span>}</div>
          <div className="truncate text-xs text-neutral-400">{sub}</div>
        </div>
        <div className="flex shrink-0 gap-2">
          <PraticaInfo kind="cfg" id={c.id} testid={`vesta-tools-cfg-info-${c.id}`} />
          {onOpen && <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]" data-testid={`vesta-tools-cfg-open-${c.id}`}><ExternalLink className="h-3.5 w-3.5" />Apri</button>}
          {reload && <CfgDelete c={c} torri={torri} reload={reload} protetta={stage(c) >= 3} />}
        </div>
      </div>
      {stage(c) >= 0 && <Progress c={c} testid={`vesta-tools-cfg-tratt-${c.id}`} />}
      {!!chips.length && <div className={`grid gap-1.5 ${chips.length > 3 ? "grid-cols-4" : "grid-cols-3"}`}>
        {chips.map(([l, v, a, k]) => <Chip key={l} label={l} value={v} accent={a} testid={`vesta-tools-cfg-tratt-${c.id}-${k || l.toLowerCase()}`} />)}
      </div>}
      {(m?.pagamento || m?.wallbox || t.fornitore_attuale) && <div className="truncate text-[11px] text-neutral-500">{[m?.wallbox, m?.pagamento, t.fornitore_attuale && `Fornitore ${t.fornitore_attuale}`].filter(Boolean).join(" · ")}</div>}
    </div>
  );
}

function Kpi({ list }) {
  const priced = list.filter((c) => c.commerciale);
  const tot = priced.reduce((a, c) => a + (Number(c.commerciale.prezzo_iva_incluso) || 0), 0);
  const withM = priced.filter((c) => c.commerciale.margine_eur != null);
  const firm = list.filter((c) => stage(c) >= 3).length;
  const items = [
    ["Totale", list.length, "n"], ["Valore", eur0(tot), "valore"],
    withM.length && ["Margine", eur0(withM.reduce((a, c) => a + Number(c.commerciale.margine_eur), 0)), "margine", true],
    ["Tasso firma", `${list.length ? Math.round((firm / list.length) * 100) : 0}%`, "firma"],
  ].filter(Boolean);
  return <div className={`grid gap-1.5 ${items.length > 3 ? "grid-cols-4" : "grid-cols-3"}`}>{items.map(([l, v, k, a]) => <Chip key={k} label={l} value={v} accent={a} testid={`vesta-cfg-kpi-${k}`} />)}</div>;
}

const pill = (on) => `shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${on ? "border-[#C17F59] bg-[#C17F59]/15 text-[#E8B48F]" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`;
const sel = "min-w-0 max-w-full truncate rounded-full border border-neutral-800 bg-neutral-950 px-3 py-1 text-[11px] text-neutral-300 outline-none focus:border-[#C17F59]";

function Toolbar({ f, setF, partners, nascoste }) {
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-[180px] flex-1 items-center gap-2 rounded-full border border-neutral-800 px-3 py-1.5 focus-within:border-[#C17F59]">
          <Search className="h-3.5 w-3.5 text-neutral-500" />
          <input value={f.q} onChange={up("q")} placeholder="Cerca cliente, partner, comune…" className="w-full bg-transparent text-xs text-white outline-none placeholder:text-neutral-600" data-testid="vesta-cfg-search" />
        </label>
        {partners.length > 1 && <select value={f.partner} onChange={up("partner")} className={`${sel} flex-1 sm:max-w-[240px]`} data-testid="vesta-cfg-partner-filter"><option value="">Tutti i partner</option>{partners.map((p) => <option key={p} value={p}>{p}</option>)}</select>}
        <select value={f.sort} onChange={up("sort")} className={sel} data-testid="vesta-cfg-sort">{Object.entries(SORTS).map(([k, l]) => <option key={k} value={k}>Ordina: {l}</option>)}</select>
      </div>
      <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {FILTRI.map((l, i) => <button key={l} type="button" className={pill(f.stato === i - 2)} onClick={() => setF({ ...f, stato: i - 2 })} data-testid={`vesta-cfg-filter-${l.toLowerCase()}`}>{l}</button>)}
        {nascoste > 0 && <button type="button" className={pill(f.stato === "nascoste")} onClick={() => setF({ ...f, stato: "nascoste" })} data-testid="vesta-cfg-filter-nascoste">Nascoste dal partner ({nascoste})</button>}
      </div>
    </div>
  );
}

export function CfgList({ cfg, net, focusId, toolLabels, onOpen, torri, reload }) {
  const [f, setF] = useState({ q: "", stato: -2, sort: "data", partner: "" });
  const [bozzeOpen, setBozzeOpen] = useState(false);
  const drafts = cfg.filter(isDraft);
  const real = cfg.filter((c) => !isDraft(c));
  const partners = useMemo(() => (net ? [...new Set(cfg.map(partnerOf).filter(Boolean))].sort() : []), [cfg, net]);
  const shown = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const key = { data: (c) => new Date(c.updated_at || c.created_at || 0).getTime(), prezzo: (c) => Number(c.commerciale?.prezzo_iva_incluso) || 0, margine: (c) => Number(c.commerciale?.margine_eur) || 0 }[f.sort];
    return real.filter((c) => (f.stato === -2 || (f.stato === "nascoste" ? !!c.nascosta_partner : stage(c) === f.stato)) && (!f.partner || partnerOf(c) === f.partner)
      && (!q || [(c.summary || {}).cliente, partnerOf(c), (c.summary || {}).comune].some((v) => (v || "").toLowerCase().includes(q))))
      .sort((a, b) => key(b) - key(a));
  }, [real, f]);
  const card = (c) => <CfgCard key={c.id} c={c} net={net} focused={focusId === c.id} toolLabel={toolLabels[c.tool]} onOpen={onOpen && (() => onOpen(c))} torri={torri} reload={reload} />;
  const draftsOpen = bozzeOpen || drafts.some((c) => c.id === focusId);
  return (
    <div className="space-y-3" data-testid="vesta-cfg-list">
      {!!real.length && <Kpi list={real} />}
      {!!real.length && <Toolbar f={f} setF={setF} partners={partners} nascoste={torri ? real.filter((c) => c.nascosta_partner).length : 0} />}
      <div className="space-y-2">{shown.map(card)}</div>
      {!!real.length && !shown.length && <p className="text-sm text-neutral-500" data-testid="vesta-cfg-no-results">Nessuna configurazione corrisponde ai filtri.</p>}
      {!!drafts.length && <div className="rounded-xl border border-dashed border-neutral-800">
        <button type="button" onClick={() => setBozzeOpen(!draftsOpen)} className="flex w-full items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-neutral-400 hover:text-white" data-testid="vesta-cfg-bozze-toggle">
          Bozze ({drafts.length})<ChevronDown className={`h-4 w-4 transition-transform ${draftsOpen ? "rotate-180" : ""}`} />
        </button>
        {draftsOpen && <div className="space-y-2 px-2 pb-2" data-testid="vesta-cfg-bozze">{drafts.map(card)}</div>}
      </div>}
    </div>
  );
}
